<template>
  <modal
    name="custom-identifier-manager"
    :classes="'customModal'"
    :scrollable="true"
    :reset="true"
    :adaptive="false"
    :width="1180"
    height="auto"
    @before-open="handleBeforeOpen"
    @closed="handleClosed"
  >
    <div class="modalWrapper">
      <div class="content__box">
        <div class="content__box__header pb-2">
          <div class="top__filter align-items-center">
            <ul class="top__filter__ul">
              <li class="cursor-default">Custom Identifiers</li>
            </ul>
            <ul class="top__filter__ul ml-auto">
              <li class="cursor-default opacity--50">
                {{ scopeLabel }}
              </li>
            </ul>
          </div>
        </div>
        <div class="content__divider"></div>
        <div class="content__box__body">
          <template>
            <div class="d-flex justify-content-between align-items-center flex-wrap mb-3" style="gap: 12px;">
              <ul class="top__filter__ul top__filter__ul--btn mb-0">
                <li :class="{ active: scopeMode === 'owner' }" @click="applyScopeMode('owner')">Owner Shared</li>
                <li :class="{ active: scopeMode === 'property' }" @click="applyScopeMode('property')">Property Specific</li>
              </ul>
              <div v-if="scopeMode === 'property'" style="min-width: 260px;">
                <select
                  v-model="selectedScopePropertyId"
                  class="textInputField"
                  :disabled="loading.scopeProperties"
                  @change="onScopePropertyChange"
                >
                  <option value="">{{ loading.scopeProperties ? 'Loading properties...' : 'Select property' }}</option>
                  <option v-for="propertyOption in scopePropertyOptions" :key="propertyOption.value" :value="propertyOption.value">
                    {{ propertyOption.text }}
                  </option>
                </select>
              </div>
            </div>
            <p class="txt--xs txt--off-white opacity--50 mb-3">
              {{ scopeDescription }}
            </p>
            <ul class="top__filter__ul top__filter__ul--btn flex-wrap mb-3">
              <li :class="{ active: activeTab === 'keys' }" @click="changeTab('keys')">Keys</li>
              <li :class="{ active: activeTab === 'values' }" @click="changeTab('values')">Predefined Values</li>
              <li :class="{ active: activeTab === 'relationships' }" @click="changeTab('relationships')">Relationships</li>
              <li :class="{ active: activeTab === 'mappings' }" @click="changeTab('mappings')">Value Mappings</li>
              <li :class="{ active: activeTab === 'cleanup' }" @click="changeTab('cleanup')">Cleanup</li>
            </ul>

            <p v-if="sectionMessage" class="txt--green txt--xs mb-3">{{ sectionMessage }}</p>
            <p v-if="sectionError" class="txt--red txt--xs mb-3">{{ sectionError }}</p>

            <div v-if="activeTab === 'keys'">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <p class="txt--off-white txt--ss mb-0">Manage active and inactive keys for the current scope.</p>
                <button type="button" class="button button--outline button--sm mt-2 mt-sm-0" @click="startCreateKey">New Key</button>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Key</label>
                    <input v-model.trim="keyForm.key" type="text" maxlength="64" class="textInputField" placeholder="Account" />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Display Name</label>
                    <input v-model.trim="keyForm.display_name" type="text" maxlength="64" class="textInputField" placeholder="Account" />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Value Type</label>
                    <select v-model="keyForm.value_type" class="textInputField">
                      <option value="predefined">Predefined</option>
                      <option value="freeform">Freeform</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-8">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Description</label>
                    <input v-model.trim="keyForm.description" type="text" class="textInputField" placeholder="Optional description" />
                  </div>
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <label class="txt--xs txt--off-white d-flex align-items-center mb-3 schema-inline-toggle">
                    <input v-model="keyForm.is_active" type="checkbox" class="mr-2" />
                    <span>Active</span>
                  </label>
                </div>
                <div v-if="keyForm.value_type === 'freeform'" class="col-md-6">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Validation Pattern</label>
                    <input v-model.trim="keyForm.validation_pattern" type="text" class="textInputField" placeholder="^[A-Za-z0-9 ]+$" />
                  </div>
                </div>
                <div v-if="keyForm.value_type === 'freeform'" class="col-md-6">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Validation Error Message</label>
                    <input v-model.trim="keyForm.validation_error_message" type="text" class="textInputField" placeholder="Invalid format" />
                  </div>
                </div>
              </div>

              <div class="d-flex flex-wrap mb-3" style="gap: 12px;">
                <button type="button" class="button button--primary button--sm" @click="submitKeyForm" :disabled="loading.keySubmit">
                  {{ loading.keySubmit ? 'Saving...' : (editingKeyId ? 'Update Key' : 'Create Key') }}
                </button>
                <button v-if="editingKeyId || hasKeyFormValues" type="button" class="button button--outline button--sm" @click="resetKeyForm">
                  Clear
                </button>
              </div>

              <div class="table-responsive schema-table-wrapper">
                <table class="table table__audit table-borderless">
                  <thead>
                    <tr>
                      <th>Display Name</th>
                      <th>Key</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>Pattern</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="keys.length > 0">
                    <tr v-for="keyDefinition in keys" :key="keyDefinition._id">
                      <td>{{ keyDefinition.display_name || keyDefinition.key }}</td>
                      <td>{{ keyDefinition.key }}</td>
                      <td>{{ keyDefinition.value_type }}</td>
                      <td>{{ keyDefinition.description || '-' }}</td>
                      <td>{{ keyDefinition.validation_pattern || '-' }}</td>
                      <td :class="keyDefinition.is_active ? 'txt--green' : 'txt--red'">
                        {{ keyDefinition.is_active ? 'Active' : 'Inactive' }}
                      </td>
                      <td>
                        <span class="cursor-pointer mr-3" @click="startEditKey(keyDefinition)">Edit</span>
                        <span class="cursor-pointer txt--primary mr-3" @click="openValuesTabForKey(keyDefinition)">Values</span>
                        <span class="cursor-pointer txt--primary" @click="openRelationshipsTabForKey(keyDefinition)">Relationships</span>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="7"><p class="text-center mb-0">No custom identifier keys found.</p></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-else-if="activeTab === 'values'">
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Predefined Key</label>
                    <select v-model="selectedKeyId" class="textInputField" @change="handleSelectedKeyChange">
                      <option value="">Select a predefined key</option>
                      <option v-for="keyOption in predefinedKeyOptions" :key="keyOption._id" :value="keyOption._id">
                        {{ keyOption.display_name || keyOption.key }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6 d-flex align-items-end justify-content-md-end">
                  <button type="button" class="button button--outline button--sm mb-3" @click="startCreateValue" :disabled="!selectedKeyId || !selectedKeyIsPredefined">
                    New Value
                  </button>
                </div>
              </div>

              <div v-if="!selectedKeyId" class="py-4 text-center">
                <p class="txt--off-white txt--ss mb-0">Choose a predefined key to manage its values.</p>
              </div>
              <div v-else-if="!selectedKeyIsPredefined" class="py-4 text-center">
                <p class="txt--off-white txt--ss mb-0">This key is freeform. Predefined values are not applicable.</p>
              </div>
              <template v-else>
                <div class="row mb-3">
                  <div class="col-md-4">
                    <div class="form__group mb-3">
                      <label class="txt--xs txt--off-white mb-2">Value</label>
                      <input v-model.trim="valueForm.value" type="text" class="textInputField" placeholder="100670" :disabled="!!editingValueId" />
                    </div>
                  </div>
                  <div class="col-md-5">
                    <div class="form__group mb-3">
                      <label class="txt--xs txt--off-white mb-2">Display Text</label>
                      <input v-model.trim="valueForm.display_text" type="text" class="textInputField" placeholder="Acme Corp (100670)" />
                    </div>
                  </div>
                  <div class="col-md-3 d-flex align-items-end">
                    <label class="txt--xs txt--off-white d-flex align-items-center mb-3 schema-inline-toggle">
                      <input v-model="valueForm.is_active" type="checkbox" class="mr-2" />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div class="d-flex flex-wrap mb-3" style="gap: 12px;">
                  <button type="button" class="button button--primary button--sm" @click="submitValueForm" :disabled="loading.valueSubmit">
                    {{ loading.valueSubmit ? 'Saving...' : (editingValueId ? 'Update Value' : 'Add Value') }}
                  </button>
                  <button v-if="editingValueId || hasValueFormValues" type="button" class="button button--outline button--sm" @click="resetValueForm">
                    Clear
                  </button>
                </div>

                <div class="table-responsive schema-table-wrapper">
                  <table class="table table__audit table-borderless">
                    <thead>
                      <tr>
                        <th>Value</th>
                        <th>Display Text</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody v-if="keyValues.length > 0">
                      <tr v-for="valueRow in keyValues" :key="valueRow._id || valueRow.value">
                        <td>{{ valueRow.value }}</td>
                        <td>{{ valueRow.display_text }}</td>
                        <td :class="valueRow.is_active === false ? 'txt--red' : 'txt--green'">
                          {{ valueRow.is_active === false ? 'Inactive' : 'Active' }}
                        </td>
                        <td>
                          <span class="cursor-pointer" @click="startEditValue(valueRow)">Edit</span>
                        </td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <tr>
                        <td colspan="4"><p class="text-center mb-0">No predefined values found for this key.</p></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>

            <div v-else-if="activeTab === 'relationships'">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <p class="txt--off-white txt--ss mb-0">Create explicit relationships between keys and optionally scope them to permit types.</p>
                <button type="button" class="button button--outline button--sm mt-2 mt-sm-0" @click="startCreateRelationship">New Relationship</button>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Source Key</label>
                    <select v-model="relationshipForm.source_key_id" class="textInputField">
                      <option value="">Select source key</option>
                      <option v-for="keyOption in activeKeyOptions" :key="keyOption._id" :value="keyOption._id">
                        {{ keyOption.display_name || keyOption.key }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Target Key</label>
                    <select v-model="relationshipForm.target_key_id" class="textInputField">
                      <option value="">Select target key</option>
                      <option v-for="keyOption in activeKeyOptions" :key="keyOption._id" :value="keyOption._id">
                        {{ keyOption.display_name || keyOption.key }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <label class="txt--xs txt--off-white d-flex align-items-center mb-3 schema-inline-toggle">
                    <input v-model="relationshipForm.is_bidirectional" type="checkbox" class="mr-2" />
                    <span>Bidirectional</span>
                  </label>
                </div>
                <div class="col-md-12">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Permit Type Scope</label>
                    <multi-list-select
                      :list="permitScopeOptions"
                      option-value="value"
                      option-text="text"
                      :selected-items="relationshipPermitScopeSelection"
                      placeholder="Leave empty to apply to all permit types"
                      class="custom-vue-select"
                      @select="updateRelationshipPermitScopeSelection"
                    />
                  </div>
                </div>
              </div>

              <div class="d-flex flex-wrap mb-3" style="gap: 12px;">
                <button type="button" class="button button--primary button--sm" @click="submitRelationshipForm" :disabled="loading.relationshipSubmit">
                  {{ loading.relationshipSubmit ? 'Saving...' : (editingRelationshipId ? 'Update Relationship' : 'Create Relationship') }}
                </button>
                <button v-if="editingRelationshipId || hasRelationshipFormValues" type="button" class="button button--outline button--sm" @click="resetRelationshipForm">
                  Clear
                </button>
              </div>

              <div class="table-responsive schema-table-wrapper">
                <table class="table table__audit table-borderless">
                  <thead>
                    <tr>
                      <th>Source Key</th>
                      <th>Target Key</th>
                      <th>Bidirectional</th>
                      <th>Permit Scope</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="relationships.length > 0">
                    <tr v-for="relationship in relationships" :key="relationship._id">
                      <td>{{ relationship.source_key }}</td>
                      <td>{{ relationship.target_key }}</td>
                      <td>{{ relationship.is_bidirectional ? 'Yes' : 'No' }}</td>
                      <td>{{ formatPermitScope(relationship.permit_type_scope) }}</td>
                      <td>
                        <span class="cursor-pointer mr-3" @click="startEditRelationship(relationship)">Edit</span>
                        <span class="cursor-pointer txt--primary mr-3" @click="openMappingsTabForRelationship(relationship)">Mappings</span>
                        <span class="cursor-pointer txt--red" @click="deleteRelationship(relationship)">Delete</span>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="5"><p class="text-center mb-0">No relationships configured for this property.</p></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-else-if="activeTab === 'mappings'">
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Relationship</label>
                    <select v-model="selectedRelationshipId" class="textInputField" @change="handleSelectedRelationshipChange">
                      <option value="">Select a relationship</option>
                      <option v-for="relationship in relationships" :key="relationship._id" :value="relationship._id">
                        {{ relationship.source_key }} -> {{ relationship.target_key }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6 d-flex align-items-end justify-content-md-end">
                  <button type="button" class="button button--outline button--sm mb-3" @click="startCreateMapping" :disabled="!selectedRelationshipId">
                    New Mapping
                  </button>
                </div>
              </div>

              <div v-if="!selectedRelationship" class="py-4 text-center">
                <p class="txt--off-white txt--ss mb-0">Choose a relationship to manage explicit value mappings.</p>
              </div>
              <template v-else>
                <div class="row mb-3">
                  <div class="col-md-5">
                    <div class="form__group mb-3">
                      <label class="txt--xs txt--off-white mb-2">Source Value</label>
                      <model-list-select
                        v-if="selectedRelationshipSourceIsPredefined"
                        v-model="mappingForm.source_value"
                        :list="mappingSourceOptions"
                        option-value="value"
                        option-text="text"
                        placeholder="Select source value"
                        class="custom-vue-select"
                      />
                      <input v-else v-model.trim="mappingForm.source_value" type="text" class="textInputField" placeholder="Source value" />
                    </div>
                  </div>
                  <div class="col-md-5">
                    <div class="form__group mb-3">
                      <label class="txt--xs txt--off-white mb-2">Target Value</label>
                      <model-list-select
                        v-if="selectedRelationshipTargetIsPredefined"
                        v-model="mappingForm.target_value"
                        :list="mappingTargetOptions"
                        option-value="value"
                        option-text="text"
                        placeholder="Select target value"
                        class="custom-vue-select"
                      />
                      <input v-else v-model.trim="mappingForm.target_value" type="text" class="textInputField" placeholder="Target value" />
                    </div>
                  </div>
                  <div class="col-md-2 d-flex align-items-end">
                    <label class="txt--xs txt--off-white d-flex align-items-center mb-3 schema-inline-toggle">
                      <input v-model="mappingForm.is_active" type="checkbox" class="mr-2" />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div class="d-flex flex-wrap mb-3" style="gap: 12px;">
                  <button type="button" class="button button--primary button--sm" @click="submitMappingForm" :disabled="loading.mappingSubmit">
                    {{ loading.mappingSubmit ? 'Saving...' : (editingMappingId ? 'Update Mapping' : 'Create Mapping') }}
                  </button>
                  <button v-if="editingMappingId || hasMappingFormValues" type="button" class="button button--outline button--sm" @click="resetMappingForm">
                    Clear
                  </button>
                </div>

                <div class="row mb-3">
                  <div class="col-md-5">
                    <div class="form__group mb-0">
                      <label class="txt--xs txt--off-white mb-2">Filter Mappings</label>
                      <input v-model.trim="mappingSearch" type="text" class="textInputField" placeholder="Search source or target values" />
                    </div>
                  </div>
                </div>

                <div class="table-responsive schema-table-wrapper">
                  <table class="table table__audit table-borderless">
                    <thead>
                      <tr>
                        <th>Source Value</th>
                        <th>Target Value</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody v-if="filteredRelationshipMappings.length > 0">
                      <tr v-for="mapping in filteredRelationshipMappings" :key="mapping._id || `${mapping.source_value}-${mapping.target_value}`">
                        <td>{{ mapping.source_value }}</td>
                        <td>{{ mapping.target_value }}</td>
                        <td :class="mapping.is_active === false ? 'txt--red' : 'txt--green'">
                          {{ mapping.is_active === false ? 'Inactive' : 'Active' }}
                        </td>
                        <td>
                          <span class="cursor-pointer mr-3" @click="startEditMapping(mapping)">Edit</span>
                          <span class="cursor-pointer txt--red" @click="deleteMapping(mapping)">Delete</span>
                        </td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <tr>
                        <td colspan="4"><p class="text-center mb-0">No value mappings found for this relationship.</p></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>

            <div v-else-if="activeTab === 'cleanup'">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <p class="txt--off-white txt--ss mb-0">Audit existing customer identifiers and run controlled bulk rewrites.</p>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Permit Filter (optional)</label>
                    <select v-model="auditFilters.permit_id" class="textInputField">
                      <option value="">All permits</option>
                      <option v-for="permitOption in permitTypeFilterOptions" :key="permitOption.value" :value="permitOption.value">
                        {{ permitOption.text }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Record Limit</label>
                    <input v-model.number="auditFilters.limit" type="number" min="1" max="200" class="textInputField" placeholder="50" />
                  </div>
                </div>
                <div class="col-md-5 d-flex align-items-end justify-content-md-end">
                  <button type="button" class="button button--primary button--sm mb-3" @click="runCustomerIdentifierAudit" :disabled="loading.audit">
                    {{ loading.audit ? 'Running Audit...' : 'Run Audit' }}
                  </button>
                </div>
              </div>

              <div v-if="auditResult && auditResult.summary" class="row mb-3">
                <div class="col-md-3">
                  <div class="form__group mb-2">
                    <label class="txt--xs txt--off-white mb-1">Subscriptions Scanned</label>
                    <p class="txt--off-white txt--ss mb-0">{{ auditResult.summary.subscriptions_scanned || 0 }}</p>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form__group mb-2">
                    <label class="txt--xs txt--off-white mb-1">With Issues</label>
                    <p class="txt--off-white txt--ss mb-0">{{ auditResult.summary.subscriptions_with_issues || 0 }}</p>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form__group mb-2">
                    <label class="txt--xs txt--off-white mb-1">Issue Counts</label>
                    <p class="txt--off-white txt--ss mb-0">
                      Unknown: {{ getIssueCount('unknown_key') }} | Duplicate: {{ getIssueCount('duplicate_key') }} | Invalid Predefined: {{ getIssueCount('invalid_predefined_value') }} | Regex: {{ getIssueCount('regex_mismatch') }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="table-responsive schema-table-wrapper mb-3" v-if="auditResult && Array.isArray(auditResult.usage_by_key)">
                <table class="table table__audit table-borderless">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Known</th>
                      <th>Type</th>
                      <th>Occurrences</th>
                      <th>Subscriptions</th>
                      <th>Invalid Occurrences</th>
                    </tr>
                  </thead>
                  <tbody v-if="auditResult.usage_by_key.length > 0">
                    <tr v-for="usageRow in auditResult.usage_by_key" :key="usageRow.key">
                      <td>{{ usageRow.display_name || usageRow.key }}</td>
                      <td>{{ usageRow.known_key ? 'Yes' : 'No' }}</td>
                      <td>{{ usageRow.value_type || '-' }}</td>
                      <td>{{ usageRow.occurrence_count || 0 }}</td>
                      <td>{{ usageRow.subscription_count || 0 }}</td>
                      <td>{{ usageRow.invalid_occurrence_count || 0 }}</td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="6"><p class="text-center mb-0">No usage rows returned by audit.</p></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="table-responsive schema-table-wrapper mb-3" v-if="auditResult && Array.isArray(auditResult.records)">
                <table class="table table__audit table-borderless">
                  <thead>
                    <tr>
                      <th>Subscription</th>
                      <th>Permit</th>
                      <th>Status</th>
                      <th>Issues</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="auditResult.records.length > 0">
                    <tr v-for="recordRow in auditResult.records" :key="recordRow.subscription_id">
                      <td>{{ recordRow.subscription_id }}</td>
                      <td>{{ recordRow.permit_id || '-' }}</td>
                      <td>{{ recordRow.status || '-' }}</td>
                      <td>
                        <span v-if="Array.isArray(recordRow.issues) && recordRow.issues.length > 0">
                          {{ recordRow.issues.map((issue) => issue.type).join(', ') }}
                        </span>
                        <span v-else>-</span>
                      </td>
                      <td>
                        <span class="cursor-pointer txt--primary" @click="openAuditRecordDrilldown(recordRow)">View Details</span>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="5"><p class="text-center mb-0">No subscription issues found.</p></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="content__divider mb-3"></div>
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <p class="txt--off-white txt--ss mb-0">Bulk Rewrite Existing Identifiers</p>
              </div>

              <div class="row mb-3">
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Action</label>
                    <select v-model="bulkForm.action" class="textInputField">
                      <option value="rename_key">rename_key</option>
                      <option value="replace_value">replace_value</option>
                      <option value="rename_and_replace">rename_and_replace</option>
                      <option value="remove_identifier">remove_identifier</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Permit Filter (optional)</label>
                    <select v-model="bulkForm.permit_id" class="textInputField">
                      <option value="">All permits</option>
                      <option v-for="permitOption in permitTypeFilterOptions" :key="`bulk-${permitOption.value}`" :value="permitOption.value">
                        {{ permitOption.text }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <label class="txt--xs txt--off-white d-flex align-items-center mb-3 schema-inline-toggle">
                    <input v-model="bulkForm.dry_run" type="checkbox" class="mr-2" />
                    <span>Dry Run</span>
                  </label>
                </div>
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Match Key</label>
                    <input v-model.trim="bulkForm.match_key" type="text" class="textInputField" placeholder="legacy_department" />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">Match Value (optional)</label>
                    <input v-model.trim="bulkForm.match_value" type="text" class="textInputField" placeholder="Finance" />
                  </div>
                </div>
                <div class="col-md-4" v-if="bulkActionNeedsNewKey">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">New Key</label>
                    <input v-model.trim="bulkForm.new_key" type="text" class="textInputField" placeholder="department" />
                  </div>
                </div>
                <div class="col-md-4" v-if="bulkActionNeedsNewValue">
                  <div class="form__group mb-3">
                    <label class="txt--xs txt--off-white mb-2">New Value</label>
                    <input v-model.trim="bulkForm.new_value" type="text" class="textInputField" placeholder="HR" />
                  </div>
                </div>
              </div>

              <div class="d-flex flex-wrap mb-3" style="gap: 12px;">
                <button type="button" class="button button--primary button--sm" @click="runBulkIdentifierRewrite" :disabled="loading.bulkUpdate">
                  {{ loading.bulkUpdate ? 'Submitting...' : (bulkForm.dry_run ? 'Preview Changes' : 'Apply Changes') }}
                </button>
                <button type="button" class="button button--outline button--sm" @click="resetCleanupForms">
                  Clear
                </button>
              </div>

              <div v-if="bulkResult" class="table-responsive schema-table-wrapper">
                <table class="table table__audit table-borderless">
                  <tbody>
                    <tr>
                      <td class="txt--off-white">Mode</td>
                      <td>{{ bulkResult.dry_run ? 'Dry Run' : 'Applied' }}</td>
                    </tr>
                    <tr>
                      <td class="txt--off-white">Subscriptions Scanned</td>
                      <td>{{ bulkResult.subscriptions_scanned || 0 }}</td>
                    </tr>
                    <tr>
                      <td class="txt--off-white">Subscriptions Matched</td>
                      <td>{{ bulkResult.subscriptions_matched || 0 }}</td>
                    </tr>
                    <tr>
                      <td class="txt--off-white">Identifier Occurrences Matched</td>
                      <td>{{ bulkResult.identifier_occurrences_matched || 0 }}</td>
                    </tr>
                    <tr>
                      <td class="txt--off-white">Sample Changes</td>
                      <td>{{ Array.isArray(bulkResult.sample_changes) ? bulkResult.sample_changes.length : 0 }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="session__modal__close" @click="hideModal">
        <i class="fas fa-times"></i>
      </div>
    </div>
  </modal>
</template>

<script>
import "vue-search-select/dist/VueSearchSelect.css";
import { ModelListSelect, MultiListSelect } from "vue-search-select";
import {
  buildSelectedCustomIdentifierKeys,
  extractApiErrorMessage,
  fetchCustomIdentifierKeyValues,
  fetchCustomIdentifierKeys,
  fetchCustomIdentifierRelationships,
  fetchCustomIdentifierRelationshipValues,
  fetchPermitTypesForProperty,
  fetchCustomerIdentifierAudit,
  bulkUpdateCustomerIdentifiers,
  normalizeRequiredCustomIdentifierKey,
  createCustomIdentifierKey,
  updateCustomIdentifierKey,
  createCustomIdentifierKeyValue,
  updateCustomIdentifierKeyValue,
  createCustomIdentifierRelationship,
  updateCustomIdentifierRelationship,
  deleteCustomIdentifierRelationship,
  createCustomIdentifierRelationshipValue,
  updateCustomIdentifierRelationshipValue,
  deleteCustomIdentifierRelationshipValue,
} from "@/services/customIdentifier.service";

function cloneState(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeBoolean(value, defaultValue = true) {
  if (value === null || value === undefined) {
    return defaultValue;
  }

  if (typeof value === "boolean") {
    return value;
  }

  return value === 1 || value === "1" || value === "true";
}

export default {
  name: "CustomIdentifierManager",
  components: {
    ModelListSelect,
    MultiListSelect,
  },
  data() {
    return {
      propertyId: "",
      propertyName: "",
      scopeMode: "owner",
      selectedScopePropertyId: "",
      scopePropertyOptions: [],
      activeTab: "keys",
      sectionMessage: "",
      sectionError: "",
      schemaCache: {},
      keyValuesCache: {},
      relationshipMappingsCache: {},
      keys: [],
      relationships: [],
      keyValues: [],
      relationshipMappings: [],
      permitScopeOptions: [],
      permitTypeFilterOptions: [],
      selectedKeyId: "",
      selectedRelationshipId: "",
      editingKeyId: "",
      editingValueId: "",
      editingRelationshipId: "",
      editingMappingId: "",
      mappingSearch: "",
      relationshipPermitScopeSelection: [],
      auditFilters: {
        permit_id: "",
        limit: 50,
      },
      auditResult: null,
      bulkForm: {
        action: "rename_and_replace",
        match_key: "",
        match_value: "",
        new_key: "",
        new_value: "",
        dry_run: true,
        permit_id: "",
      },
      bulkResult: null,
      keyForm: {
        key: "",
        display_name: "",
        description: "",
        value_type: "predefined",
        validation_pattern: "",
        validation_error_message: "",
        is_active: true,
      },
      valueForm: {
        value: "",
        display_text: "",
        is_active: true,
      },
      relationshipForm: {
        source_key_id: "",
        target_key_id: "",
        is_bidirectional: true,
      },
      mappingForm: {
        source_value: "",
        target_value: "",
        is_active: true,
      },
      loading: {
        schema: false,
        scopeProperties: false,
        keyValues: false,
        mappings: false,
        audit: false,
        bulkUpdate: false,
        keySubmit: false,
        valueSubmit: false,
        relationshipSubmit: false,
        mappingSubmit: false,
      },
    };
  },
  computed: {
    selectedScopePropertyName() {
      const selectedId = normalizeRequiredCustomIdentifierKey(this.selectedScopePropertyId);
      if (!selectedId) {
        return "";
      }

      const selectedProperty = this.scopePropertyOptions.find((option) => normalizeRequiredCustomIdentifierKey(option.value) === selectedId);
      return selectedProperty ? selectedProperty.text : "";
    },
    scopeLabel() {
      if (this.scopeMode === "property") {
        return `Property scope: ${this.selectedScopePropertyName || this.propertyName || "Select property"}`;
      }
      return "Owner scope: Shared across all properties";
    },
    scopeDescription() {
      if (this.scopeMode === "property") {
        if (!normalizeRequiredCustomIdentifierKey(this.propertyId)) {
          return "Select a property to manage property-specific schema definitions. Owner-level shared definitions are included in property scope reads.";
        }
        return "You are managing property-specific schema definitions. Owner-level shared definitions are included when listing keys and relationships.";
      }
      return "You are managing owner-level shared schema definitions that apply across all properties.";
    },
    activeKeyOptions() {
      return this.keys.filter((keyDefinition) => keyDefinition && keyDefinition.is_active !== false);
    },
    predefinedKeyOptions() {
      return this.activeKeyOptions.filter((keyDefinition) => String(keyDefinition.value_type || "") === "predefined");
    },
    selectedKey() {
      return this.keys.find((keyDefinition) => String(keyDefinition._id) === String(this.selectedKeyId)) || null;
    },
    selectedKeyIsPredefined() {
      return !!this.selectedKey && String(this.selectedKey.value_type || "") === "predefined";
    },
    selectedRelationship() {
      return this.relationships.find((relationship) => String(relationship._id) === String(this.selectedRelationshipId)) || null;
    },
    selectedRelationshipSourceKeyDefinition() {
      if (!this.selectedRelationship) {
        return null;
      }

      return this.findKeyDefinition(this.selectedRelationship.source_key_id, this.selectedRelationship.source_key);
    },
    selectedRelationshipTargetKeyDefinition() {
      if (!this.selectedRelationship) {
        return null;
      }

      return this.findKeyDefinition(this.selectedRelationship.target_key_id, this.selectedRelationship.target_key);
    },
    selectedRelationshipSourceIsPredefined() {
      return !!this.selectedRelationshipSourceKeyDefinition && String(this.selectedRelationshipSourceKeyDefinition.value_type || "") === "predefined";
    },
    selectedRelationshipTargetIsPredefined() {
      return !!this.selectedRelationshipTargetKeyDefinition && String(this.selectedRelationshipTargetKeyDefinition.value_type || "") === "predefined";
    },
    mappingSourceOptions() {
      if (!this.selectedRelationshipSourceKeyDefinition) {
        return [];
      }

      return this.getKeyValueOptions(this.selectedRelationshipSourceKeyDefinition._id);
    },
    mappingTargetOptions() {
      if (!this.selectedRelationshipTargetKeyDefinition) {
        return [];
      }

      return this.getKeyValueOptions(this.selectedRelationshipTargetKeyDefinition._id);
    },
    filteredRelationshipMappings() {
      const searchText = normalizeRequiredCustomIdentifierKey(this.mappingSearch).toLowerCase();
      if (!searchText) {
        return this.relationshipMappings;
      }

      return this.relationshipMappings.filter((mapping) => {
        const sourceValue = normalizeRequiredCustomIdentifierKey(mapping && mapping.source_value).toLowerCase();
        const targetValue = normalizeRequiredCustomIdentifierKey(mapping && mapping.target_value).toLowerCase();
        return sourceValue.includes(searchText) || targetValue.includes(searchText);
      });
    },
    hasKeyFormValues() {
      return !!(
        this.keyForm.key ||
        this.keyForm.display_name ||
        this.keyForm.description ||
        this.keyForm.validation_pattern ||
        this.keyForm.validation_error_message ||
        this.keyForm.value_type !== "predefined" ||
        this.keyForm.is_active === false
      );
    },
    hasValueFormValues() {
      return !!(this.valueForm.value || this.valueForm.display_text || this.valueForm.is_active === false);
    },
    hasRelationshipFormValues() {
      return !!(
        this.relationshipForm.source_key_id ||
        this.relationshipForm.target_key_id ||
        this.relationshipPermitScopeSelection.length > 0 ||
        this.relationshipForm.is_bidirectional === false
      );
    },
    hasMappingFormValues() {
      return !!(this.mappingForm.source_value || this.mappingForm.target_value || this.mappingForm.is_active === false);
    },
    bulkActionNeedsNewKey() {
      return this.bulkForm.action === "rename_key" || this.bulkForm.action === "rename_and_replace";
    },
    bulkActionNeedsNewValue() {
      return this.bulkForm.action === "replace_value" || this.bulkForm.action === "rename_and_replace";
    },
  },
  methods: {
    async fetchScopeProperties() {
      this.loading.scopeProperties = true;

      try {
        const response = await this.axios.post(window.hostname + "/api/auth/all-space", {
          device_type: 1,
          limit: 500,
          page: 1,
          search_param: "",
          space_type: 1,
          status: 4,
          minify: 1,
          minify_2: 1,
        });

        const allSpace = response && response.data && response.data.data && Array.isArray(response.data.data.all_space)
          ? response.data.data.all_space
          : [];

        this.scopePropertyOptions = allSpace
          .map((property) => ({
            value: normalizeRequiredCustomIdentifierKey(property && property.space_id),
            text: normalizeRequiredCustomIdentifierKey(property && (property.property_name || property.address || property.space_name || property.space_id)),
          }))
          .filter((property) => property.value && property.text);
      } catch (error) {
        this.scopePropertyOptions = [];
      } finally {
        this.loading.scopeProperties = false;
      }
    },
    async applyScopeMode(mode) {
      if (mode !== "owner" && mode !== "property") {
        return;
      }

      this.scopeMode = mode;
      this.clearFeedback();

      if (mode === "owner") {
        this.selectedScopePropertyId = "";
        this.propertyId = "";
        this.propertyName = "All properties";
        await this.loadPropertySchema("", true);
        return;
      }

      if (this.scopePropertyOptions.length === 0) {
        await this.fetchScopeProperties();
      }

      const selectedPropertyId = normalizeRequiredCustomIdentifierKey(this.selectedScopePropertyId)
        || normalizeRequiredCustomIdentifierKey(this.propertyId)
        || (this.scopePropertyOptions[0] ? normalizeRequiredCustomIdentifierKey(this.scopePropertyOptions[0].value) : "");

      if (!selectedPropertyId) {
        this.propertyId = "";
        this.propertyName = "";
        this.keys = [];
        this.relationships = [];
        this.keyValues = [];
        this.relationshipMappings = [];
        this.permitScopeOptions = [];
        this.setSectionError("No properties available for property-specific scope.");
        return;
      }

      this.selectedScopePropertyId = selectedPropertyId;
      await this.onScopePropertyChange();
    },
    async onScopePropertyChange() {
      const selectedPropertyId = normalizeRequiredCustomIdentifierKey(this.selectedScopePropertyId);
      if (!selectedPropertyId) {
        this.propertyId = "";
        this.propertyName = "";
        return;
      }

      const selectedProperty = this.scopePropertyOptions.find(
        (propertyOption) => normalizeRequiredCustomIdentifierKey(propertyOption.value) === selectedPropertyId
      );

      this.propertyId = selectedPropertyId;
      this.propertyName = selectedProperty ? selectedProperty.text : "Selected property";
      await this.loadPropertySchema(this.propertyId, true);
    },
    async handleBeforeOpen(event) {
      const params = event && event.params ? event.params : {};
      this.propertyId = normalizeRequiredCustomIdentifierKey(params.propertyId);
      this.propertyName = normalizeRequiredCustomIdentifierKey(params.propertyName);
      this.scopeMode = this.propertyId ? "property" : "owner";
      this.selectedScopePropertyId = this.propertyId;
      if (!this.propertyName && !this.propertyId) {
        this.propertyName = "All properties";
      }
      this.activeTab = "keys";
      this.sectionMessage = "";
      this.sectionError = "";
      this.resetAllForms();

      await this.fetchScopeProperties();

      if (this.scopeMode === "property" && this.propertyId && !this.propertyName) {
        const selectedProperty = this.scopePropertyOptions.find(
          (propertyOption) => normalizeRequiredCustomIdentifierKey(propertyOption.value) === this.propertyId
        );
        this.propertyName = selectedProperty ? selectedProperty.text : "Selected property";
      }

      await this.loadPropertySchema(this.propertyId);
    },
    handleClosed() {
      this.propertyId = "";
      this.propertyName = "";
      this.scopeMode = "owner";
      this.selectedScopePropertyId = "";
      this.scopePropertyOptions = [];
      this.activeTab = "keys";
      this.sectionMessage = "";
      this.sectionError = "";
      this.mappingSearch = "";
      this.keys = [];
      this.relationships = [];
      this.keyValues = [];
      this.relationshipMappings = [];
      this.permitScopeOptions = [];
      this.permitTypeFilterOptions = [];
      this.selectedKeyId = "";
      this.selectedRelationshipId = "";
      this.auditResult = null;
      this.bulkResult = null;
      this.resetCleanupForms();
      this.resetAllForms();
    },
    hideModal() {
      this.$modal.hide("custom-identifier-manager");
    },
    resetAllForms() {
      this.resetKeyForm();
      this.resetValueForm();
      this.resetRelationshipForm();
      this.resetMappingForm();
    },
    resetCleanupForms() {
      this.auditFilters = {
        permit_id: "",
        limit: 50,
      };
      this.bulkForm = {
        action: "rename_and_replace",
        match_key: "",
        match_value: "",
        new_key: "",
        new_value: "",
        dry_run: true,
        permit_id: "",
      };
    },
    resetKeyForm() {
      this.editingKeyId = "";
      this.keyForm = {
        key: "",
        display_name: "",
        description: "",
        value_type: "predefined",
        validation_pattern: "",
        validation_error_message: "",
        is_active: true,
      };
    },
    resetValueForm() {
      this.editingValueId = "";
      this.valueForm = {
        value: "",
        display_text: "",
        is_active: true,
      };
    },
    resetRelationshipForm() {
      this.editingRelationshipId = "";
      this.relationshipForm = {
        source_key_id: "",
        target_key_id: "",
        is_bidirectional: true,
      };
      this.relationshipPermitScopeSelection = [];
    },
    resetMappingForm() {
      this.editingMappingId = "";
      this.mappingForm = {
        source_value: "",
        target_value: "",
        is_active: true,
      };
    },
    setSectionMessage(message) {
      this.sectionMessage = normalizeRequiredCustomIdentifierKey(message);
      this.sectionError = "";
    },
    setSectionError(message) {
      this.sectionError = normalizeRequiredCustomIdentifierKey(message);
      this.sectionMessage = "";
    },
    clearFeedback() {
      this.sectionMessage = "";
      this.sectionError = "";
    },
    getSchemaScopeKey() {
      return this.propertyId || "__global__";
    },
    getPropertyCache() {
      const scopeKey = this.getSchemaScopeKey();

      if (!this.schemaCache[scopeKey]) {
        this.schemaCache[scopeKey] = {
          keys: [],
          relationships: [],
          permitScopeOptions: [],
          permitTypeFilterOptions: [],
        };
      }

      if (!this.keyValuesCache[scopeKey]) {
        this.keyValuesCache[scopeKey] = {};
      }

      if (!this.relationshipMappingsCache[scopeKey]) {
        this.relationshipMappingsCache[scopeKey] = {};
      }

      return this.schemaCache[scopeKey];
    },
    syncSchemaCache() {
      const propertyCache = this.getPropertyCache();
      if (!propertyCache) {
        return;
      }

      propertyCache.keys = cloneState(this.keys);
      propertyCache.relationships = cloneState(this.relationships);
      propertyCache.permitScopeOptions = cloneState(this.permitScopeOptions);
      propertyCache.permitTypeFilterOptions = cloneState(this.permitTypeFilterOptions);
    },
    hydrateFromCache() {
      const propertyCache = this.getPropertyCache();
      if (!propertyCache) {
        return false;
      }

      this.keys = cloneState(propertyCache.keys || []);
      this.relationships = cloneState(propertyCache.relationships || []);
      this.permitScopeOptions = cloneState(propertyCache.permitScopeOptions || []);
      this.permitTypeFilterOptions = cloneState(propertyCache.permitTypeFilterOptions || []);
      this.ensureSelectedKey();
      this.ensureSelectedRelationship();
      return this.keys.length > 0 || this.relationships.length > 0 || this.permitScopeOptions.length > 0 || this.permitTypeFilterOptions.length > 0;
    },
    ensureSelectedKey() {
      if (this.selectedKeyId && this.keys.some((keyDefinition) => String(keyDefinition._id) === String(this.selectedKeyId))) {
        return;
      }

      const nextKey = this.predefinedKeyOptions[0] || this.activeKeyOptions[0] || null;
      this.selectedKeyId = nextKey ? String(nextKey._id) : "";
    },
    ensureSelectedRelationship() {
      if (this.selectedRelationshipId && this.relationships.some((relationship) => String(relationship._id) === String(this.selectedRelationshipId))) {
        return;
      }

      this.selectedRelationshipId = this.relationships.length > 0 ? String(this.relationships[0]._id) : "";
    },
    async loadPropertySchema(propertyId, force = false) {
      this.clearFeedback();
      this.propertyId = normalizeRequiredCustomIdentifierKey(propertyId);
      if (!force && this.hydrateFromCache()) {
        await this.ensureActiveTabDataLoaded();
        return;
      }

      this.loading.schema = true;
      try {
        const [keys, relationships, permitTypes] = await Promise.all([
          fetchCustomIdentifierKeys(this.axios, window.hostname, this.propertyId),
          fetchCustomIdentifierRelationships(this.axios, window.hostname, this.propertyId),
          fetchPermitTypesForProperty(this.axios, window.hostname, this.propertyId),
        ]);

        this.keys = Array.isArray(keys) ? keys : [];
        this.relationships = Array.isArray(relationships) ? relationships : [];
        this.permitScopeOptions = this.buildPermitScopeOptions(permitTypes);
        this.permitTypeFilterOptions = this.buildPermitTypeFilterOptions(permitTypes);
        this.ensureSelectedKey();
        this.ensureSelectedRelationship();
        this.syncSchemaCache();
        await this.ensureActiveTabDataLoaded();
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to load custom identifier schema."));
      } finally {
        this.loading.schema = false;
      }
    },
    buildPermitScopeOptions(permitTypes) {
      const seen = new Set();
      return (Array.isArray(permitTypes) ? permitTypes : [])
        .map((permit) => normalizeRequiredCustomIdentifierKey(permit && (permit.permit_name || permit.permit_title || permit.name)))
        .filter((permitName) => {
          if (!permitName || seen.has(permitName)) {
            return false;
          }
          seen.add(permitName);
          return true;
        })
        .map((permitName) => ({ value: permitName, text: permitName }));
    },
    buildPermitTypeFilterOptions(permitTypes) {
      return (Array.isArray(permitTypes) ? permitTypes : [])
        .map((permit) => {
          const value = normalizeRequiredCustomIdentifierKey(permit && (permit.permit_id || permit._id || permit.id));
          const text = normalizeRequiredCustomIdentifierKey(permit && (permit.permit_name || permit.permit_title || permit.name));
          return { value, text };
        })
        .filter((option) => option.value && option.text);
    },
    async ensureActiveTabDataLoaded() {
      if (this.activeTab === "values" && this.selectedKeyIsPredefined) {
        await this.loadSelectedKeyValues();
      }

      if (this.activeTab === "mappings" && this.selectedRelationshipId) {
        await this.loadSelectedRelationshipMappings();
      }

      if (this.activeTab === "cleanup" && !this.auditResult && !this.loading.audit) {
        await this.runCustomerIdentifierAudit();
      }
    },
    getIssueCount(issueType) {
      const issueCounts = this.auditResult && this.auditResult.summary && this.auditResult.summary.issue_counts
        ? this.auditResult.summary.issue_counts
        : {};
      const value = issueCounts[issueType];
      return typeof value === "number" ? value : 0;
    },
    escapeHtml(value) {
      return String(value === null || value === undefined ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    renderRecordIssuesHtml(recordRow) {
      const issues = Array.isArray(recordRow && recordRow.issues) ? recordRow.issues : [];
      if (issues.length === 0) {
        return "<p style=\"margin: 0;\">No issues reported.</p>";
      }

      const rows = issues.map((issue) => {
        const type = this.escapeHtml(issue && issue.type);
        const key = this.escapeHtml(issue && issue.key);
        const value = this.escapeHtml(issue && issue.value);
        const message = this.escapeHtml(issue && issue.message);
        return `<tr><td style="padding: 4px 8px;">${type || "-"}</td><td style="padding: 4px 8px;">${key || "-"}</td><td style="padding: 4px 8px;">${value || "-"}</td><td style="padding: 4px 8px;">${message || "-"}</td></tr>`;
      }).join("");

      return `<table style="width: 100%; border-collapse: collapse;"><thead><tr><th style="text-align:left; padding: 4px 8px;">Type</th><th style="text-align:left; padding: 4px 8px;">Key</th><th style="text-align:left; padding: 4px 8px;">Value</th><th style="text-align:left; padding: 4px 8px;">Message</th></tr></thead><tbody>${rows}</tbody></table>`;
    },
    openAuditRecordDrilldown(recordRow) {
      const safeRecord = recordRow || {};
      const customIdentifiers = Array.isArray(safeRecord.custom_identifiers) ? safeRecord.custom_identifiers : [];
      const customIdentifiersJson = this.escapeHtml(JSON.stringify(customIdentifiers, null, 2));
      const subscriptionId = this.escapeHtml(safeRecord.subscription_id || "-");
      const permitId = this.escapeHtml(safeRecord.permit_id || "-");
      const userId = this.escapeHtml(safeRecord.user_id || "-");
      const status = this.escapeHtml(safeRecord.status || "-");
      const issuesHtml = this.renderRecordIssuesHtml(safeRecord);

      const html = `
        <div style="text-align: left; max-height: 70vh; overflow-y: auto;">
          <p><strong>Subscription:</strong> ${subscriptionId}</p>
          <p><strong>Permit:</strong> ${permitId}</p>
          <p><strong>User:</strong> ${userId}</p>
          <p><strong>Status:</strong> ${status}</p>
          <hr />
          <p><strong>Issues</strong></p>
          ${issuesHtml}
          <hr />
          <p><strong>Custom Identifiers</strong></p>
          <pre style="white-space: pre-wrap; background: #0f1828; padding: 10px; border-radius: 6px;">${customIdentifiersJson}</pre>
        </div>
      `;

      this.$swal({
        title: "Audit Record Details",
        html,
        width: 980,
        showConfirmButton: true,
        confirmButtonText: "Close",
      });
    },
    async runCustomerIdentifierAudit() {
      this.clearFeedback();
      this.loading.audit = true;

      try {
        const payload = {
          limit: this.auditFilters.limit || 50,
        };
        if (this.propertyId) {
          payload.property_id = this.propertyId;
        }
        if (this.auditFilters.permit_id) {
          payload.permit_id = this.auditFilters.permit_id;
        }

        const response = await fetchCustomerIdentifierAudit(this.axios, window.hostname, payload);
        if (!response || response.status === false) {
          this.setSectionError((response && response.message) || "Unable to run customer identifier audit.");
          return;
        }

        this.auditResult = response.data || null;
        this.setSectionMessage(response.message || "Customer identifier audit completed.");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to run customer identifier audit."));
      } finally {
        this.loading.audit = false;
      }
    },
    validateBulkForm() {
      if (!normalizeRequiredCustomIdentifierKey(this.bulkForm.match_key)) {
        return "Match key is required.";
      }

      if (this.bulkActionNeedsNewKey && !normalizeRequiredCustomIdentifierKey(this.bulkForm.new_key)) {
        return "New key is required for this action.";
      }

      if (this.bulkActionNeedsNewValue && !normalizeRequiredCustomIdentifierKey(this.bulkForm.new_value)) {
        return "New value is required for this action.";
      }

      return "";
    },
    async runBulkIdentifierRewrite() {
      this.clearFeedback();
      const validationMessage = this.validateBulkForm();
      if (validationMessage) {
        this.setSectionError(validationMessage);
        return;
      }

      this.loading.bulkUpdate = true;
      try {
        const payload = {
          action: this.bulkForm.action,
          match_key: normalizeRequiredCustomIdentifierKey(this.bulkForm.match_key),
          dry_run: this.bulkForm.dry_run,
        };

        const matchValue = normalizeRequiredCustomIdentifierKey(this.bulkForm.match_value);
        if (matchValue) {
          payload.match_value = matchValue;
        }

        if (this.bulkActionNeedsNewKey) {
          payload.new_key = normalizeRequiredCustomIdentifierKey(this.bulkForm.new_key);
        }

        if (this.bulkActionNeedsNewValue) {
          payload.new_value = normalizeRequiredCustomIdentifierKey(this.bulkForm.new_value);
        }

        if (this.propertyId) {
          payload.property_id = this.propertyId;
        }

        if (this.bulkForm.permit_id) {
          payload.permit_id = this.bulkForm.permit_id;
        }

        const response = await bulkUpdateCustomerIdentifiers(this.axios, window.hostname, payload);
        const responseData = response && response.data ? response.data : {};

        if (responseData.status === false) {
          this.setSectionError(responseData.message || "Unable to run bulk update.");
          return;
        }

        this.bulkResult = responseData.data || null;
        this.setSectionMessage(responseData.message || "Customer identifier cleanup completed.");
        await this.runCustomerIdentifierAudit();
        this.$emit("updated");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to run customer identifier bulk update."));
      } finally {
        this.loading.bulkUpdate = false;
      }
    },
    async loadSelectedKeyValues(force = false) {
      if (!this.selectedKeyId || !this.selectedKeyIsPredefined) {
        this.keyValues = [];
        return;
      }

      const scopeKey = this.getSchemaScopeKey();
      const propertyKeyValuesCache = this.keyValuesCache[scopeKey] || {};
      if (!force && propertyKeyValuesCache[this.selectedKeyId]) {
        this.keyValues = cloneState(propertyKeyValuesCache[this.selectedKeyId]);
        return;
      }

      this.loading.keyValues = true;
      try {
        const values = await fetchCustomIdentifierKeyValues(this.axios, window.hostname, this.selectedKeyId);
        this.keyValues = Array.isArray(values) ? values : [];
        this.keyValuesCache[scopeKey][this.selectedKeyId] = cloneState(this.keyValues);
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to load predefined values."));
      } finally {
        this.loading.keyValues = false;
      }
    },
    async loadKeyValuesForKeyId(keyId, force = false) {
      if (!keyId) {
        return [];
      }

      const normalizedKeyId = String(keyId);
      const scopeKey = this.getSchemaScopeKey();
      const propertyKeyValuesCache = this.keyValuesCache[scopeKey] || {};
      if (!force && propertyKeyValuesCache[normalizedKeyId]) {
        return cloneState(propertyKeyValuesCache[normalizedKeyId]);
      }

      const values = await fetchCustomIdentifierKeyValues(this.axios, window.hostname, normalizedKeyId);
      const normalizedValues = Array.isArray(values) ? values : [];
      this.keyValuesCache[scopeKey][normalizedKeyId] = cloneState(normalizedValues);
      return normalizedValues;
    },
    async loadSelectedRelationshipMappings(force = false) {
      if (!this.selectedRelationshipId) {
        this.relationshipMappings = [];
        return;
      }

      const scopeKey = this.getSchemaScopeKey();
      const propertyRelationshipMappingsCache = this.relationshipMappingsCache[scopeKey] || {};
      if (!force && propertyRelationshipMappingsCache[this.selectedRelationshipId]) {
        this.relationshipMappings = cloneState(propertyRelationshipMappingsCache[this.selectedRelationshipId]);
      } else {
        this.loading.mappings = true;
        try {
          const mappings = await fetchCustomIdentifierRelationshipValues(this.axios, window.hostname, this.selectedRelationshipId);
          this.relationshipMappings = Array.isArray(mappings) ? mappings : [];
          this.relationshipMappingsCache[scopeKey][this.selectedRelationshipId] = cloneState(this.relationshipMappings);
        } catch (error) {
          this.setSectionError(extractApiErrorMessage(error, "Unable to load relationship mappings."));
        } finally {
          this.loading.mappings = false;
        }
      }

      if (this.selectedRelationshipSourceIsPredefined && this.selectedRelationshipSourceKeyDefinition) {
        await this.loadKeyValuesForKeyId(this.selectedRelationshipSourceKeyDefinition._id);
      }
      if (this.selectedRelationshipTargetIsPredefined && this.selectedRelationshipTargetKeyDefinition) {
        await this.loadKeyValuesForKeyId(this.selectedRelationshipTargetKeyDefinition._id);
      }
    },
    changeTab(tab) {
      this.activeTab = tab;
      this.clearFeedback();
      this.ensureActiveTabDataLoaded();
    },
    findKeyDefinition(keyId, keyName) {
      if (keyId) {
        const matchedById = this.keys.find((keyDefinition) => String(keyDefinition._id) === String(keyId));
        if (matchedById) {
          return matchedById;
        }
      }

      const normalizedKeyName = normalizeRequiredCustomIdentifierKey(keyName);
      return this.keys.find((keyDefinition) => normalizeRequiredCustomIdentifierKey(keyDefinition.key) === normalizedKeyName) || null;
    },
    getKeyValueOptions(keyId) {
      if (!keyId) {
        return [];
      }

      const scopeKey = this.getSchemaScopeKey();
      const values = (this.keyValuesCache[scopeKey] || {})[String(keyId)] || [];
      return values
        .filter((valueRow) => valueRow && valueRow.is_active !== false)
        .map((valueRow) => ({
          value: valueRow.value,
          text: valueRow.display_text || valueRow.value,
        }));
    },
    formatPermitScope(scopeValues) {
      const scope = Array.isArray(scopeValues) ? scopeValues.filter((value) => normalizeRequiredCustomIdentifierKey(value) !== "") : [];
      return scope.length > 0 ? scope.join(", ") : "All permit types";
    },
    buildPermitScopeSelection(scopeValues) {
      const knownOptions = this.permitScopeOptions.reduce((accumulator, option) => {
        accumulator[normalizeRequiredCustomIdentifierKey(option && option.value)] = option;
        return accumulator;
      }, {});

      return buildSelectedCustomIdentifierKeys(scopeValues).map((item) => {
        const knownOption = knownOptions[normalizeRequiredCustomIdentifierKey(item && item.value)];
        return knownOption ? cloneState(knownOption) : item;
      });
    },
    updateRelationshipPermitScopeSelection(items) {
      this.relationshipPermitScopeSelection = Array.isArray(items) ? items : [];
    },
    handleSelectedKeyChange() {
      this.resetValueForm();
      this.clearFeedback();
      this.loadSelectedKeyValues();
    },
    async handleSelectedRelationshipChange() {
      this.resetMappingForm();
      this.mappingSearch = "";
      this.clearFeedback();
      await this.loadSelectedRelationshipMappings();
    },
    startCreateKey() {
      this.resetKeyForm();
      this.clearFeedback();
    },
    startEditKey(keyDefinition) {
      this.editingKeyId = String(keyDefinition._id || "");
      this.keyForm = {
        key: normalizeRequiredCustomIdentifierKey(keyDefinition.key),
        display_name: normalizeRequiredCustomIdentifierKey(keyDefinition.display_name) || normalizeRequiredCustomIdentifierKey(keyDefinition.key),
        description: normalizeRequiredCustomIdentifierKey(keyDefinition.description),
        value_type: normalizeRequiredCustomIdentifierKey(keyDefinition.value_type) || "predefined",
        validation_pattern: normalizeRequiredCustomIdentifierKey(keyDefinition.validation_pattern),
        validation_error_message: normalizeRequiredCustomIdentifierKey(keyDefinition.validation_error_message),
        is_active: normalizeBoolean(keyDefinition.is_active, true),
      };
      this.clearFeedback();
    },
    async submitKeyForm() {
      const key = normalizeRequiredCustomIdentifierKey(this.keyForm.key);
      if (!key) {
        this.setSectionError("Key is required.");
        return;
      }

      if (key.length > 64) {
        this.setSectionError("Key cannot be longer than 64 characters.");
        return;
      }

      if (!normalizeRequiredCustomIdentifierKey(this.keyForm.display_name)) {
        this.setSectionError("Display name is required.");
        return;
      }

      const payload = {
        key,
        display_name: normalizeRequiredCustomIdentifierKey(this.keyForm.display_name),
        description: normalizeRequiredCustomIdentifierKey(this.keyForm.description) || null,
        value_type: this.keyForm.value_type === "freeform" ? "freeform" : "predefined",
        validation_pattern: this.keyForm.value_type === "freeform"
          ? normalizeRequiredCustomIdentifierKey(this.keyForm.validation_pattern) || null
          : null,
        validation_error_message: this.keyForm.value_type === "freeform"
          ? normalizeRequiredCustomIdentifierKey(this.keyForm.validation_error_message) || null
          : null,
        is_active: this.keyForm.is_active,
      };
      if (this.propertyId) {
        payload.property_id = this.propertyId;
      }

      this.loading.keySubmit = true;
      try {
        const response = this.editingKeyId
          ? await updateCustomIdentifierKey(this.axios, window.hostname, this.editingKeyId, payload)
          : await createCustomIdentifierKey(this.axios, window.hostname, payload);

        this.setSectionMessage((response && response.data && response.data.message) || "Key saved successfully.");
        await this.loadPropertySchema(this.propertyId, true);
        this.resetKeyForm();
        this.$emit("updated");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to save key."));
      } finally {
        this.loading.keySubmit = false;
      }
    },
    openValuesTabForKey(keyDefinition) {
      this.selectedKeyId = String(keyDefinition && keyDefinition._id ? keyDefinition._id : "");
      this.changeTab("values");
    },
    openRelationshipsTabForKey(keyDefinition) {
      this.changeTab("relationships");
      if (!this.relationshipForm.source_key_id) {
        this.relationshipForm.source_key_id = String(keyDefinition && keyDefinition._id ? keyDefinition._id : "");
      }
    },
    startCreateValue() {
      this.resetValueForm();
      this.clearFeedback();
    },
    startEditValue(valueRow) {
      this.editingValueId = String(valueRow && valueRow._id ? valueRow._id : "");
      this.valueForm = {
        value: normalizeRequiredCustomIdentifierKey(valueRow.value),
        display_text: normalizeRequiredCustomIdentifierKey(valueRow.display_text) || normalizeRequiredCustomIdentifierKey(valueRow.value),
        is_active: normalizeBoolean(valueRow.is_active, true),
      };
      this.clearFeedback();
    },
    async submitValueForm() {
      if (!this.selectedKeyId || !this.selectedKeyIsPredefined) {
        this.setSectionError("Choose a predefined key before saving values.");
        return;
      }

      const value = normalizeRequiredCustomIdentifierKey(this.valueForm.value);
      if (!value) {
        this.setSectionError("Value is required.");
        return;
      }

      const payload = {
        value,
        display_text: normalizeRequiredCustomIdentifierKey(this.valueForm.display_text) || value,
        is_active: this.valueForm.is_active,
      };

      this.loading.valueSubmit = true;
      try {
        const response = this.editingValueId
          ? await updateCustomIdentifierKeyValue(this.axios, window.hostname, this.selectedKeyId, this.editingValueId, payload)
          : await createCustomIdentifierKeyValue(this.axios, window.hostname, this.selectedKeyId, payload);

        this.setSectionMessage((response && response.data && response.data.message) || "Value saved successfully.");
        await this.loadSelectedKeyValues(true);
        this.resetValueForm();
        this.$emit("updated");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to save predefined value."));
      } finally {
        this.loading.valueSubmit = false;
      }
    },
    startCreateRelationship() {
      this.resetRelationshipForm();
      this.clearFeedback();
    },
    startEditRelationship(relationship) {
      this.editingRelationshipId = String(relationship && relationship._id ? relationship._id : "");
      this.relationshipForm = {
        source_key_id: normalizeRequiredCustomIdentifierKey(relationship.source_key_id),
        target_key_id: normalizeRequiredCustomIdentifierKey(relationship.target_key_id),
        is_bidirectional: normalizeBoolean(relationship.is_bidirectional, false),
      };
      this.relationshipPermitScopeSelection = this.buildPermitScopeSelection(relationship.permit_type_scope || []);
      this.clearFeedback();
    },
    async submitRelationshipForm() {
      if (!this.relationshipForm.source_key_id || !this.relationshipForm.target_key_id) {
        this.setSectionError("Source and target keys are required.");
        return;
      }

      if (String(this.relationshipForm.source_key_id) === String(this.relationshipForm.target_key_id)) {
        this.setSectionError("Source and target keys must be different.");
        return;
      }

      const payload = {
        source_key_id: this.relationshipForm.source_key_id,
        target_key_id: this.relationshipForm.target_key_id,
        is_bidirectional: this.relationshipForm.is_bidirectional,
        permit_type_scope: this.relationshipPermitScopeSelection.map((item) => item.value),
      };
      if (this.propertyId) {
        payload.property_id = this.propertyId;
      }

      this.loading.relationshipSubmit = true;
      try {
        const response = this.editingRelationshipId
          ? await updateCustomIdentifierRelationship(this.axios, window.hostname, this.editingRelationshipId, payload)
          : await createCustomIdentifierRelationship(this.axios, window.hostname, payload);

        this.setSectionMessage((response && response.data && response.data.message) || "Relationship saved successfully.");
        await this.loadPropertySchema(this.propertyId, true);
        this.resetRelationshipForm();
        this.$emit("updated");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to save relationship."));
      } finally {
        this.loading.relationshipSubmit = false;
      }
    },
    openMappingsTabForRelationship(relationship) {
      this.selectedRelationshipId = String(relationship && relationship._id ? relationship._id : "");
      this.changeTab("mappings");
    },
    async deleteRelationship(relationship) {
      const decision = await this.$swal({
        title: "Delete relationship?",
        text: `Remove ${relationship.source_key} -> ${relationship.target_key}?`,
        showCancelButton: true,
      });

      if (!decision.value && !decision.isConfirmed) {
        return;
      }

      try {
        const response = await deleteCustomIdentifierRelationship(this.axios, window.hostname, relationship._id);
        const scopeKey = this.getSchemaScopeKey();
        if (!this.relationshipMappingsCache[scopeKey]) {
          this.relationshipMappingsCache[scopeKey] = {};
        }
        delete this.relationshipMappingsCache[scopeKey][String(relationship._id)];
        if (String(this.selectedRelationshipId) === String(relationship._id)) {
          this.selectedRelationshipId = "";
          this.relationshipMappings = [];
        }
        this.setSectionMessage((response && response.data && response.data.message) || "Relationship deleted successfully.");
        await this.loadPropertySchema(this.propertyId, true);
        this.$emit("updated");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to delete relationship."));
      }
    },
    startCreateMapping() {
      this.resetMappingForm();
      this.clearFeedback();
    },
    startEditMapping(mapping) {
      this.editingMappingId = String(mapping && mapping._id ? mapping._id : "");
      this.mappingForm = {
        source_value: normalizeRequiredCustomIdentifierKey(mapping.source_value),
        target_value: normalizeRequiredCustomIdentifierKey(mapping.target_value),
        is_active: normalizeBoolean(mapping.is_active, true),
      };
      this.clearFeedback();
    },
    async submitMappingForm() {
      if (!this.selectedRelationshipId || !this.selectedRelationship) {
        this.setSectionError("Choose a relationship before saving mappings.");
        return;
      }

      const sourceValue = normalizeRequiredCustomIdentifierKey(this.mappingForm.source_value);
      const targetValue = normalizeRequiredCustomIdentifierKey(this.mappingForm.target_value);
      if (!sourceValue || !targetValue) {
        this.setSectionError("Source and target values are required.");
        return;
      }

      const payload = {
        source_value: sourceValue,
        target_value: targetValue,
        is_active: this.mappingForm.is_active,
      };

      this.loading.mappingSubmit = true;
      try {
        const response = this.editingMappingId
          ? await updateCustomIdentifierRelationshipValue(this.axios, window.hostname, this.selectedRelationshipId, this.editingMappingId, payload)
          : await createCustomIdentifierRelationshipValue(this.axios, window.hostname, this.selectedRelationshipId, payload);

        this.setSectionMessage((response && response.data && response.data.message) || "Mapping saved successfully.");
        await this.loadSelectedRelationshipMappings(true);
        this.resetMappingForm();
        this.$emit("updated");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to save relationship mapping."));
      } finally {
        this.loading.mappingSubmit = false;
      }
    },
    async deleteMapping(mapping) {
      const decision = await this.$swal({
        title: "Delete mapping?",
        text: `Remove mapping ${mapping.source_value} -> ${mapping.target_value}?`,
        showCancelButton: true,
      });

      if (!decision.value && !decision.isConfirmed) {
        return;
      }

      try {
        const response = await deleteCustomIdentifierRelationshipValue(this.axios, window.hostname, this.selectedRelationshipId, mapping._id);
        this.setSectionMessage((response && response.data && response.data.message) || "Mapping deleted successfully.");
        await this.loadSelectedRelationshipMappings(true);
        this.$emit("updated");
      } catch (error) {
        this.setSectionError(extractApiErrorMessage(error, "Unable to delete mapping."));
      }
    },
  },
};
</script>

<style scoped>
.schema-table-wrapper {
  max-height: 420px;
}

.schema-inline-toggle input {
  width: 14px;
  height: 14px;
}
</style>